// Global
import { withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useEffect, useRef, useState } from 'react';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';

import classNames from 'classnames';
import { ComponentProps } from 'lib/types/component-props';
//import BodyCopy from 'src/helpers/BodyCopy/BodyCopy';
//import Headline from 'src/helpers/Headline/Headline';
//import AuthWidget from 'src/components/tool/AuthWidget/AuthWidget';

import styles from './TechDocChatBot.module.css';
import { useRouter } from 'next/router';
import Link from 'next/link';

// Add console logging for debugging
if (typeof window !== 'undefined') {
  console.log('[TechDocChatBot] Component loaded and ready');
}

export type TechDocChatbotProps =
  Feature.EnterpriseWeb.AndersenWindows.Components.Tool.TechDocChatbot.TechDocChatbot &
    ComponentProps;

const formatPages = (pages: SourceRef['page_numbers']) => {
  if (Array.isArray(pages)) {
    return pages.join(', ');
  }
  if (typeof pages === 'string') {
    return pages;
  }
  return '';
};

interface SourceRef {
  file_name?: string | null;
  page_numbers?: number[] | string | null;
  raw_text?: string | null; // optional preview
  url?: string | null; // future-proof if backend adds links
}

interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  sources?: SourceRef[];
  backendMessageId?: number;
  backendSessionId?: number;
  feedback?: 'up' | 'down' | undefined;
  feedbackPending?: boolean;
  feedbackReason?: string | null;
  isThinking?: boolean;
}

const TechDocChatbot = (props: TechDocChatbotProps) => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [sessionId, setSessionId] = useState<number | undefined>(undefined);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      text: 'Hi, I am the Andersen AI Technical ChatBot Assistant!\n\nHow can I help you today?',
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const SHOW_SOURCES = false;
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const MAX_INPUT_LENGTH = 200;

  const [feedbackModal, setFeedbackModal] = useState<{
    open: boolean;
    messageId?: string;
    text: string;
  }>({ open: false, messageId: undefined, text: '' });

  const [me, setMe] = useState<{
    username: string;
    email?: string | null;
    display_name?: string | null;
    auth_provider?: string;
    roles?: string[] | null;
  } | null>(null);

  const [authModal, setAuthModal] = useState<{
    open: boolean;
    mode: 'login' | 'register';
    username: string;
    email: string;
    displayName: string;
    password: string;
    busy: boolean;
    error: string | null;
  }>({
    open: false,
    mode: 'login',
    username: '',
    email: '',
    displayName: '',
    password: '',
    busy: false,
    error: null,
  });

  const SHOW_AUTH_UI = false;

  async function loadMe() {
    try {
      const resp = await fetch('/api/auth/me');
      if (!resp.ok) {
        setMe(null);
        return;
      }
      const data = await resp.json();
      setMe(data);
    } catch {
      setMe(null);
    }
  }

  useEffect(() => {
    loadMe();
  }, []);

  function resetChat() {
    setSessionId(undefined);
    setMessages([
      {
        id: '1',
        text: "Hello! I'm your TechDoc assistant. I can help you with questions about Andersen Windows & Doors products, technical documentation, and installation procedures. What would you like to know?",
        isUser: false,
        timestamp: new Date(),
      },
    ]);
  }

  type Me = {
    username: string;
    email?: string | null;
    display_name?: string | null;
    auth_provider?: string;
    roles?: string[] | null;
  } | null;

  // Helper (note: param name isn't "me" to avoid confusion)
  function accountLabel(user: Me): string {
    if (!user) {
      return 'Guest';
    }

    const username = (user.username || '').trim();
    const displayName = (user.display_name || '').trim();

    // Andersen SSO: everything before the "@"
    if (user.auth_provider === 'entra' && username.includes('@')) {
      return username.split('@')[0];
    }

    // Local account: first 6 chars + "..."
    const u = username || displayName;
    if (!u) {
      return 'Guest';
    }
    return u.length > 6 ? `${u.slice(0, 6)}...` : u;
  }

  function openAuthModalDefault() {
    setAuthModal({
      open: true,
      mode: 'login',
      username: '',
      email: '',
      displayName: '',
      password: '',
      busy: false,
      error: null,
    });
  }

  function hasRole(user: Me, role: string): boolean {
    if (!user || !Array.isArray(user.roles)) {
      return false;
    }
    return user.roles.includes(role);
  }

  async function doLogin() {
    setAuthModal((s) => ({ ...s, busy: true, error: null }));
    try {
      const resp = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: authModal.username.trim(),
          password: authModal.password,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setAuthModal((s) => ({
          ...s,
          busy: false,
          error: data?.detail || data?.error || 'Login failed',
        }));
        return;
      }
      await loadMe();
      setAuthModal((s) => ({ ...s, open: false, busy: false }));
    } catch (e: any) {
      setAuthModal((s) => ({ ...s, busy: false, error: String(e?.message ?? e) }));
    }
  }

  async function doRegister() {
    setAuthModal((s) => ({ ...s, busy: true, error: null }));
    try {
      const resp = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: authModal.username.trim(),
          email: authModal.email.trim() || null,
          password: authModal.password,
          display_name: authModal.displayName.trim() || null,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setAuthModal((s) => ({
          ...s,
          busy: false,
          error: data?.detail || data?.error || 'Registration failed',
        }));
        return;
      }
      // auto-login after register
      await doLogin();
    } catch (e: any) {
      setAuthModal((s) => ({ ...s, busy: false, error: String(e?.message ?? e) }));
    }
  }

  async function doLogout() {
    try {
      await fetch('/api/auth/logout');
    } catch {}
    setMe(null);
    resetChat();
  }

  const [showMoreInfoModal, setShowMoreInfoModal] = useState(false);

  // Detect mobile/tablet/desktop with debounce for performance
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const checkViewport = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    const debouncedCheck = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkViewport, 150);
    };

    checkViewport();
    window.addEventListener('resize', debouncedCheck);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedCheck);
    };
  }, []);
  async function postFeedback(opts: {
    messageId: number;
    isPositive: boolean;
    commentText?: string | null;
    reasonCode?: string | null;
  }) {
    const body = {
      message_id: opts.messageId,
      is_positive: opts.isPositive,
      comment_text: opts.commentText ?? null,
      reason_code: opts.reasonCode ?? null,
      session_id: sessionId ?? null,
    };

    const resp = await fetch('/api/ai/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await resp.json();
    if (!resp.ok) {
      throw new Error(data?.detail || data?.error || `Feedback failed (${resp.status})`);
    }
    return data;
  }

  function setMessage(idx: number, patch: Partial<ChatMessage>) {
    setMessages((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  }

  function patchMessageById(id: string, patch: Partial<ChatMessage>) {
    setMessages((prev) => {
      const i = prev.findIndex((m) => m.id === id);
      if (i === -1) {
        return prev;
      }
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      return next;
    });
  }

  async function handleThumbUp(idx: number) {
    const m = messages[idx];
    if (!m.backendMessageId) {
      return;
    }

    try {
      setMessage(idx, { feedbackPending: true });

      // If flipping from down -> up, send empty/null reason/comment to clear it
      await postFeedback({
        messageId: m.backendMessageId,
        isPositive: true,
        commentText: null,
        reasonCode: null,
      });

      setMessage(idx, { feedback: 'up', feedbackPending: false, feedbackReason: null });
    } catch (e) {
      console.error(e);
      setMessage(idx, { feedbackPending: false });
    }
  }

  function handleThumbDown(idx: number) {
    const m = messages[idx];
    if (!m?.backendMessageId) {
      return;
    }
    setFeedbackModal({ open: true, messageId: m.id, text: m?.feedbackReason ?? '' });
  }

  async function submitThumbDown(withoutExplanation = false) {
    const id = feedbackModal.messageId!;
    const idx = messages.findIndex((x) => x.id === id);
    if (idx < 0) {
      return;
    }
    const m = messages[idx];
    if (!m?.backendMessageId) {
      return;
    }

    try {
      setMessage(idx, { feedbackPending: true });

      const explanation = withoutExplanation ? null : feedbackModal.text.trim() || null;

      await postFeedback({
        messageId: m.backendMessageId,
        isPositive: false,
        // ReasonCode optional; you could map to a small set later. For now leave null.
        reasonCode: null,
        commentText: explanation,
      });

      setMessage(idx, { feedback: 'down', feedbackPending: false, feedbackReason: explanation });
    } catch (e) {
      console.error(e);
      setMessage(idx, { feedbackPending: false });
    } finally {
      setFeedbackModal({ open: false, messageId: undefined, text: '' });
    }
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isLoading]);

  const handleSendMessage = async () => {
    if (!inputText.trim()) {
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: inputText.trim(),
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const question = inputText.trim();
    setInputText('');
    setIsLoading(true);

    const thinkingId = `thinking-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: thinkingId,
        text: 'Thinking...',
        isUser: false,
        timestamp: new Date(),
        isThinking: true,
      },
    ]);

    try {
      const payload = { question, verbose: false, session_id: sessionId ?? null };
      console.log('POST /ai/answer payload', payload);

      const resp = await fetch('/api/ai/answer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await resp.json();
      if (!resp.ok) {
        const errMsg = data?.detail || data?.error || `Upstream error (${resp.status})`;
        throw new Error(errMsg);
      }

      if (!sessionId || sessionId !== data.session_id) {
        setSessionId(data.session_id);
      }

      let responseText = data?.answer || 'No response content.';

      // Remove source citations from the text (e.g., "(Source: file.pdf pages 1, 2; ...)")
      responseText = responseText.replace(/\s*\(Source:[\s\S]*?\)\s*$/gi, '').trim();

      const sourceDocs = Array.isArray(data?.sources)
        ? data.sources
        : Array.isArray(data?.source_documents)
        ? data.source_documents
        : [];

      patchMessageById(thinkingId, {
        id: (Date.now() + 1).toString(), // give it a new stable id if you want
        text: responseText,
        isThinking: false,
        sources: sourceDocs,
        backendMessageId: data.message_id,
        backendSessionId: data.session_id,
      });
    } catch (error) {
      console.error('API Error:', error);
      patchMessageById(thinkingId, {
        text: `Backend Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        isThinking: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClose = () => {
    if (messages.length > 1) {
      setShowCloseConfirm(true);
    } else {
      setIsOpen(false);
    }
  };

  const confirmClose = () => {
    setShowCloseConfirm(false);
    setIsOpen(false);
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    setIsOpen(false);
  };

  const handleExpand = () => {
    if (isMinimized) {
      setIsMinimized(false);
      setIsOpen(true);
    } else {
      setIsOpen(true);
    }
  };

  if (!props.fields) {
    return <></>;
  }

  // Responsive chatbot dimensions and positioning
  const getChatDimensions = () => {
    if (isMobile) {
      return {
        width: '100vw',
        height: '100dvh', // Use dvh for better mobile viewport handling
        bottom: 0,
        right: 0,
        borderRadius: 0,
      };
    }
    if (isTablet) {
      return {
        width: 'min(380px, calc(100vw - 48px))',
        height: 'min(calc(100vh - 48px), 90vh)',
        bottom: '24px',
        right: '24px',
        borderRadius: '16px',
      };
    }
    return {
      width: 'min(420px, calc(100vw - 48px))',
      height: 'min(750px, calc(100vh - 48px))',
      bottom: '24px',
      right: '24px',
      borderRadius: '16px',
    };
  };

  const chatDimensions = getChatDimensions();

  return (
    <>
      {/* Floating Chat Button - Responsive positioning */}
      {!isOpen && !isMinimized && (
        <button
          onClick={handleExpand}
          className="fixed bottom-4 right-4 z-50 h-14 w-14
                     touch-manipulation rounded-full shadow-2xl transition-transform
                     duration-200 hover:scale-110 active:scale-95 sm:bottom-6
                     sm:right-6 sm:h-[60px]
                     sm:w-[60px]"
          style={{
            backgroundColor: '#ED6833',
            // Safe area padding for devices with notches
            paddingBottom: 'max(0px, env(safe-area-inset-bottom))',
            paddingRight: 'max(0px, env(safe-area-inset-right))',
          }}
          aria-label="Open chat"
        >
          <div className="flex items-center justify-center">
            <img
              src="/chatbot-icon.svg"
              alt="ChatBot"
              className="h-6 w-6 sm:h-[30px] sm:w-[30px]"
            />
          </div>
        </button>
      )}

      {/* Minimized Chat Header - Responsive */}
      {isMinimized && (
        <div
          onClick={handleExpand}
          className="fixed bottom-4 right-4 z-50 w-64 cursor-pointer touch-manipulation
                     rounded-t-2xl p-3 shadow-2xl transition-shadow
                     hover:shadow-xl sm:bottom-6
                     sm:right-6 sm:w-[280px]
                     sm:p-4"
          style={{
            backgroundColor: '#ED6833',
          }}
        >
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center space-x-2">
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center sm:h-16 sm:w-16">
                <img src="/chatbot-icon.svg" alt="ChatBot" className="h-10 w-10 sm:h-12 sm:w-12" />
              </div>
              <span className="truncate text-sm font-bold sm:text-base">Tech Doc ChatBot</span>
            </div>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              className="flex-shrink-0"
            >
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </div>
        </div>
      )}

      {/* Main Chat Window - Fully Responsive */}
      {isOpen && (
        <div
          className={classNames(
            'fixed z-50 flex max-h-full max-w-full flex-col overflow-hidden bg-white shadow-2xl',
            styles.chatbotContainer
          )}
          style={{
            width: chatDimensions.width,
            height: chatDimensions.height,
            bottom: chatDimensions.bottom,
            right: chatDimensions.right,
            borderRadius: chatDimensions.borderRadius,
            // Ensure safe areas are respected on mobile
            paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : undefined,
          }}
        >
          {/* Chat Header - Orange #ED6833 - Responsive */}
          <div
            className="flex flex-shrink-0 items-center justify-between p-3 text-white sm:p-4"
            style={{ backgroundColor: '#ED6833', minHeight: isMobile ? '60px' : '72px' }}
          >
            {/* LEFT: logo + title */}
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center sm:h-16 sm:w-16">
                <img
                  src="/chatbot-icon.svg"
                  alt="ChatBot"
                  className="h-12 w-12 sm:h-[60px] sm:w-[60px]"
                />
              </div>
              <h3 className="truncate text-xl font-bold text-white sm:text-2xl">
                Tech Doc ChatBot
              </h3>
            </div>

            {/* RIGHT: Online + label + account icon (+ controls) */}
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: '#43EE7D' }} />
                <span className="text-sm font-medium" style={{ color: '#43EE7D' }}>
                  Online
                </span>
              </div>

              {/* compact account label (Guest / SSO user / local user) */}
              {SHOW_AUTH_UI && (
                <span className="text-[12px] text-white opacity-90">{accountLabel(me)}</span>
              )}
              {/* Account icon button */}
              {SHOW_AUTH_UI && (
                <button
                  onClick={() => {
                    me ? setAccountModalOpen(true) : openAuthModalDefault();
                  }}
                  title={me ? me.display_name || me.username : 'Sign in'}
                  aria-label={me ? 'Account' : 'Sign in'}
                  className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white/20 hover:bg-white/30"
                >
                  {me ? (
                    // filled user icon
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 12c2.761 0 5-2.686 5-6s-2.239-6-5-6-5 2.686-5 6 2.239 6 5 6Zm0 2c-4.418 0-8 2.686-8 6v2h16v-2c0-3.314-3.582-6-8-6Z" />
                    </svg>
                  ) : (
                    // outline user icon
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  )}
                </button>
              )}

              {/* Minimize (desktop only) */}
              {!isMobile && (
                <button
                  onClick={handleMinimize}
                  className="flex h-7 w-7 items-center justify-center transition-opacity hover:opacity-80 active:opacity-60 sm:h-8 sm:w-8"
                  aria-label="Minimize"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
              )}

              {/* Close */}
              <button
                onClick={handleClose}
                className="flex h-7 w-7 items-center justify-center transition-opacity hover:opacity-80 active:opacity-60 sm:h-8 sm:w-8"
                aria-label="Close"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* AI Disclaimer - Responsive text size */}
          <div
            className={classNames(
              styles.disclaimer,
              'text-gray-600 border-gray-200 flex-shrink-0 border-b bg-white px-3 py-2 text-center sm:px-4'
            )}
          >
            AI-generated responses.{' '}
            <span
              className={classNames(
                styles.disclaimerLink,
                'hover:text-gray-800 cursor-pointer underline'
              )}
              onClick={() => setShowMoreInfoModal(true)}
            >
              More information
            </span>
          </div>

          {/* Messages Area - Responsive padding and spacing */}
          <div
            className={classNames(
              'min-h-0 flex-1 space-y-3 overflow-y-auto overflow-x-hidden overscroll-contain p-3 sm:space-y-4 sm:p-4',
              styles.chatbotMessages,
              styles.chatbotScrollbar
            )}
            style={{
              backgroundColor: '#FFFFFF',
            }}
          >
            {messages.map((message, idx) => (
              <div key={message.id}>
                <div className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}>
                  <div className="flex max-w-[85%] flex-col sm:max-w-[75%]">
                    <div
                      className={`text-gray-800 rounded-2xl px-3 py-2 sm:px-4 sm:py-3`}
                      style={
                        message.isUser
                          ? { backgroundColor: '#FEF3EE', border: '1px solid #ED6833' }
                          : { backgroundColor: '#F8F6F4' }
                      }
                    >
                      {message.isThinking ? (
                        <div className={styles.thinkingDots}>
                          <span className={styles.thinkingDot}></span>
                          <span className={styles.thinkingDot}></span>
                          <span className={styles.thinkingDot}></span>
                          <span className={styles.thinkingDot}></span>
                        </div>
                      ) : (
                        <p
                          className={classNames(
                            'text-gray-800',
                            'text-sm',
                            'leading-relaxed',
                            '[&_a]:font-semibold',
                            '[&_a]:text-inherit',
                            '[&_a]:underline',
                            '[&_a]:decoration-[#ED6833]',
                            '[&_a]:underline-offset-2',
                            '[&_a:hover]:opacity-80'
                          )}
                          dangerouslySetInnerHTML={{ __html: message.text }}
                        />
                      )}
                      {SHOW_SOURCES && !message.isUser && message.sources?.length ? (
                        <div className="mt-4">
                          <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent flex gap-3 overflow-x-auto pb-2">
                            {message.sources.map((s, idx) => (
                              <a
                                key={idx}
                                href={s.url || '#'}
                                download={s.file_name || 'document.pdf'}
                                rel="noopener noreferrer"
                                className="border-gray-200 group w-40 flex-shrink-0 cursor-pointer rounded-lg border bg-white p-3 transition-shadow hover:shadow-md"
                              >
                                <div className="flex flex-col items-center gap-2 text-center">
                                  {/* PDF Icon with Download Indicator */}
                                  <div className="relative">
                                    <svg
                                      width="40"
                                      height="40"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#ED6833"
                                      strokeWidth="1.5"
                                      className="flex-shrink-0"
                                    >
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                      <polyline points="14 2 14 8 20 8" />
                                      <text
                                        x="7"
                                        y="17"
                                        fontSize="6"
                                        fill="#ED6833"
                                        fontWeight="bold"
                                      >
                                        PDF
                                      </text>
                                    </svg>
                                    {/* Small download arrow overlay */}
                                    <div className="absolute -bottom-1 -right-1 rounded-full bg-[#ED6833] p-1 opacity-0 transition-opacity group-hover:opacity-100">
                                      <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="white"
                                        strokeWidth="3"
                                      >
                                        <path d="M12 5v14M19 12l-7 7-7-7" />
                                      </svg>
                                    </div>
                                  </div>

                                  {/* Document Name */}
                                  <div className="text-gray-800 text-xs font-medium transition-colors line-clamp-2 group-hover:text-[#ED6833]">
                                    {s.file_name || 'Document'}
                                  </div>

                                  {/* Page Numbers */}
                                  {s.page_numbers ? (
                                    <div className="text-gray-500 text-[11px]">
                                      Page {formatPages(s.page_numbers)}
                                    </div>
                                  ) : null}
                                </div>
                              </a>
                            ))}

                            {/* Navigation Arrow if more than 2 sources */}
                            {message.sources.length > 2 && (
                              <div className="flex w-10 flex-shrink-0 items-center justify-center">
                                <div className="bg-gray-100 flex h-8 w-8 items-center justify-center rounded-full">
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#595959"
                                    strokeWidth="2"
                                  >
                                    <polyline points="9 18 15 12 9 6" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : null}
                      {!message.isUser && !message.isThinking && message.backendMessageId && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5 sm:gap-2">
                          <span className="text-gray-600 text-[11px] sm:text-xs">
                            Was this helpful?
                          </span>
                          <button
                            aria-label="Thumbs up"
                            aria-pressed={message.feedback === 'up'}
                            onClick={() => handleThumbUp(idx)}
                            disabled={message.feedbackPending}
                            className={`hover:bg-gray-200 active:bg-gray-300 flex min-h-[32px] min-w-[32px]
                                      touch-manipulation items-center justify-center rounded p-1.5 transition-colors
                                      sm:min-h-0 sm:min-w-0 sm:p-1
                                      ${message.feedback === 'up' ? 'bg-gray-200' : ''}`}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill={message.feedback === 'up' ? '#595959' : 'none'}
                              stroke={message.feedback === 'up' ? '#595959' : '#6b7280'}
                              strokeWidth="2"
                              className="h-4 w-4"
                            >
                              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                            </svg>
                          </button>

                          <button
                            aria-label="Thumbs down"
                            aria-pressed={message.feedback === 'down'}
                            onClick={() => handleThumbDown(idx)}
                            disabled={message.feedbackPending}
                            className={`hover:bg-gray-200 active:bg-gray-300 flex min-h-[32px] min-w-[32px]
                                      touch-manipulation items-center justify-center rounded p-1.5 transition-colors
                                      sm:min-h-0 sm:min-w-0 sm:p-1
                                      ${message.feedback === 'down' ? 'bg-gray-200' : ''}`}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill={message.feedback === 'down' ? '#595959' : 'none'}
                              stroke={message.feedback === 'down' ? '#595959' : '#6b7280'}
                              strokeWidth="2"
                              className="h-4 w-4"
                            >
                              <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
                            </svg>
                          </button>

                          {message.feedbackPending && (
                            <span className="text-gray-500 ml-1 text-[11px]">saving…</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Timestamp */}
                <div
                  className={`text-gray-500 mt-1 text-[11px] sm:text-xs ${
                    message.isUser ? 'text-right' : 'text-left'
                  }`}
                >
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}

            <div aria-hidden style={{ height: 8 }} />
            <div ref={messagesEndRef} />
          </div>

          {/* Action Buttons - Responsive */}
          <div className={styles.actionButtonsContainer}>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                className="hover:bg-gray-100 active:bg-gray-200 text-gray-900 min-h-[44px] flex-1 touch-manipulation rounded-full
                         py-2 px-3 text-xs
                         font-medium transition-colors sm:min-h-0 sm:text-sm"
                style={{ backgroundColor: '#F8F6F4' }}
              >
                Find another document
              </button>
              <button
                className="hover:bg-gray-100 active:bg-gray-200 text-gray-900 min-h-[44px] flex-1 touch-manipulation rounded-full
                         py-2 px-3 text-xs
                         font-medium transition-colors sm:min-h-0 sm:text-sm"
                style={{ backgroundColor: '#F8F6F4' }}
              >
                Contact an Expert
              </button>
            </div>
          </div>
          {feedbackModal.open && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
              <div
                className="mx-4 w-full max-w-[420px] rounded-2xl bg-white p-4 shadow-xl
                            sm:mx-0 sm:p-5"
              >
                <h4 className="mb-2 text-sm font-semibold sm:text-base">
                  Tell us what went wrong (optional)
                </h4>
                <textarea
                  value={feedbackModal.text}
                  onChange={(e) => setFeedbackModal((s) => ({ ...s, text: e.target.value }))}
                  placeholder="What was inaccurate, missing, or unhelpful?"
                  className="border-gray-300 h-24 w-full resize-y rounded-md border px-3 py-2 text-sm
                           leading-6 outline-none focus:border-[#ED6833] focus:ring-1 focus:ring-[#ED6833] sm:h-28"
                />
                <div className="mt-3 flex flex-col items-stretch justify-end gap-2 sm:flex-row sm:items-center">
                  <button
                    onClick={() =>
                      setFeedbackModal({ open: false, messageId: undefined, text: '' })
                    }
                    className="border-gray-400 text-gray-700 hover:bg-gray-100 active:bg-gray-200
                             order-3 min-h-[44px] touch-manipulation rounded-full border px-4
                             py-2 text-xs sm:order-1 sm:min-h-0 sm:text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => submitThumbDown(true)}
                    className="border-gray-400 text-gray-700 hover:bg-gray-100 active:bg-gray-200
                             order-2 min-h-[44px] touch-manipulation rounded-full border px-4
                             py-2 text-xs sm:min-h-0 sm:text-sm"
                  >
                    Submit without explanation
                  </button>
                  <button
                    onClick={() => submitThumbDown(false)}
                    className="order-1 min-h-[44px] touch-manipulation rounded-full bg-[#ED6833] px-4 py-2
                             text-xs font-semibold text-white
                             hover:opacity-90 active:opacity-80 sm:order-3 sm:min-h-0 sm:text-sm"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          )}

          {SHOW_AUTH_UI && authModal.open && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
              <div className="w-[420px] rounded-2xl bg-white p-4 shadow-xl">
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-base font-semibold">
                    {authModal.mode === 'login' ? 'Sign in' : 'Create your account'}
                  </h4>
                  <select
                    value={authModal.mode}
                    onChange={(e) =>
                      setAuthModal((s) => ({
                        ...s,
                        mode: e.target.value as 'login' | 'register',
                        error: null,
                      }))
                    }
                    className="border-gray-300 rounded-md border px-2 py-1 text-sm"
                  >
                    <option value="login">Login</option>
                    <option value="register">Register</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <input
                    value={authModal.username}
                    onChange={(e) => setAuthModal((s) => ({ ...s, username: e.target.value }))}
                    placeholder="Username"
                    className="border-gray-300 w-full rounded-md border px-3 py-2 text-sm leading-6 outline-none focus:border-[#ED6833]"
                  />
                  {authModal.mode === 'register' && (
                    <>
                      <input
                        value={authModal.email}
                        onChange={(e) => setAuthModal((s) => ({ ...s, email: e.target.value }))}
                        placeholder="Email (optional)"
                        className="border-gray-300 w-full rounded-md border px-3 py-2 text-sm leading-6 outline-none focus:border-[#ED6833]"
                      />
                      <input
                        value={authModal.displayName}
                        onChange={(e) =>
                          setAuthModal((s) => ({ ...s, displayName: e.target.value }))
                        }
                        placeholder="Display name (optional)"
                        className="border-gray-300 w-full rounded-md border px-3 py-2 text-sm leading-6 outline-none focus:border-[#ED6833]"
                      />
                    </>
                  )}
                  <input
                    type="password"
                    value={authModal.password}
                    onChange={(e) => setAuthModal((s) => ({ ...s, password: e.target.value }))}
                    placeholder="Password"
                    className="border-gray-300 w-full rounded-md border px-3 py-2 text-sm leading-6 outline-none focus:border-[#ED6833]"
                  />

                  {authModal.error && (
                    <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
                      {authModal.error}
                    </div>
                  )}
                </div>

                {/* Andersen SSO CTA */}
                <div className="mt-3">
                  <div className="flex items-center gap-2">
                    <div className="bg-gray-200 h-px flex-1" />
                    <span className="text-gray-500 text-[11px]">or</span>
                    <div className="bg-gray-200 h-px flex-1" />
                  </div>

                  <button
                    onClick={() => {
                      window.location.href = '/api/auth/entra/login';
                    }}
                    className="border-gray-300 hover:bg-gray-50 mt-3 w-full rounded-full border px-4 py-2 text-sm font-medium"
                  >
                    Login with your Andersen account
                  </button>
                </div>

                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    onClick={() => setAuthModal((s) => ({ ...s, open: false }))}
                    className="border-gray-400 text-gray-700 hover:bg-gray-100 rounded-full border px-4 py-2 text-sm"
                  >
                    Cancel
                  </button>
                  {authModal.mode === 'login' ? (
                    <button
                      onClick={doLogin}
                      disabled={authModal.busy || !authModal.username || !authModal.password}
                      className="rounded-full bg-[#ED6833] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                    >
                      {authModal.busy ? 'Signing in…' : 'Sign in'}
                    </button>
                  ) : (
                    <button
                      onClick={doRegister}
                      disabled={authModal.busy || !authModal.username || !authModal.password}
                      className="rounded-full bg-[#ED6833] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                    >
                      {authModal.busy ? 'Creating…' : 'Create account'}
                    </button>
                  )}
                </div>

                <p className="text-gray-500 mt-2 text-center text-[11px]">
                  You can chat without signing in. Signing in lets us attribute feedback and
                  sessions to you.
                </p>
              </div>
            </div>
          )}

          {SHOW_AUTH_UI && accountModalOpen && me && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
              <div className="w-[420px] rounded-2xl bg-white p-4 shadow-xl">
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-base font-semibold">Account</h4>
                  <button
                    onClick={() => setAccountModalOpen(false)}
                    className="text-gray-600 hover:bg-gray-100 rounded-md px-2 py-1 text-sm"
                  >
                    Close
                  </button>
                </div>

                <div className="mb-3">
                  <div className="text-gray-500 text-xs">Signed in as</div>
                  <div className="text-gray-900 truncate text-sm font-semibold">
                    {me.display_name || me.username}
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-end gap-2">
                  {hasRole(me, 'Admin') && (
                    <Link href="/concurrency-ai-chatbot-poc/admin">Admin Panel</Link>
                  )}
                  {hasRole(me, 'Admin') && (
                    <button
                      onClick={() => {
                        // Option A: open a modal (see below).
                        // setAdminModalOpen(true);

                        // Option B (recommended): go to the dedicated Admin page.
                        router.push('/concurrency-ai-chatbot-poc/admin');
                      }}
                      className="rounded-full bg-[#ED6833] px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90"
                    >
                      Open Admin
                    </button>
                  )}
                  <button
                    onClick={() => setAccountModalOpen(false)}
                    className="border-gray-400 text-gray-700 hover:bg-gray-100 rounded-full border px-4 py-2 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      await doLogout();
                      setAccountModalOpen(false);
                    }}
                    className="rounded-full bg-[#ED6833] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Input Area - Bottom - Responsive */}
          <div className={styles.inputArea}>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                value={inputText}
                onChange={(e) => {
                  if (e.target.value.length <= MAX_INPUT_LENGTH) {
                    setInputText(e.target.value);
                  }
                }}
                onKeyDown={handleKeyPress}
                placeholder="Type your message here..."
                className={styles.chatInput}
                disabled={isLoading}
              />
              <span className={styles.charCounter}>
                {inputText.length}/{MAX_INPUT_LENGTH}
              </span>
              <button
                onClick={handleSendMessage}
                disabled={!inputText.trim() || isLoading}
                className={styles.sendButton}
                aria-label="Send message"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#F8F6F4"
                  strokeWidth="2"
                >
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          </div>

          {/* Support Footer - Responsive */}
          <div
            className={classNames(
              'flex-shrink-0 bg-white px-3 pb-3 sm:px-4 sm:pb-4',
              isMobile ? styles.chatbotSafeAreaBottom : ''
            )}
          >
            <p className={styles.supportFooter}>
              Contact us at{' '}
              <a
                href="tel:18009333626"
                className={styles.supportLink}
                aria-label="Call Andersen support at 1 800 933 3626"
              >
                1-800-933-3626
              </a>
            </p>
          </div>

          {/* More Information Modal - Contained within chatbot */}
          {showMoreInfoModal && (
            <div
              className="fixed z-[60] flex items-center justify-center p-4"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                width: chatDimensions.width,
                height: chatDimensions.height,
                bottom: chatDimensions.bottom,
                right: chatDimensions.right,
                borderRadius: chatDimensions.borderRadius,
              }}
              onClick={() => setShowMoreInfoModal(false)}
            >
              <div
                className="relative w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setShowMoreInfoModal(false)}
                  className="absolute top-3 right-3 flex h-7 w-7 items-center justify-center rounded bg-black transition-opacity hover:opacity-70"
                  aria-label="Close"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2.5"
                  >
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>

                <h2 className="mb-3 pr-6 text-lg font-bold">More Information</h2>

                <p className="text-gray-700 mb-3 text-sm leading-relaxed">
                  Legal and privacy statement from Andersen Windows and Doors company. Lorem ipsum
                  dolor sit amet, consectetur adipiscing elit. Phasellus massa massa, auctor at
                  facilisis vel.
                </p>

                <a
                  href="#"
                  className="text-sm text-blue-600 underline transition-colors hover:text-blue-800"
                >
                  Privacy Statement
                </a>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Close Confirmation Dialog - Responsive */}
      {showCloseConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="mx-4 w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl sm:p-6">
            <h3 className="mb-2 text-base font-bold sm:mb-3 sm:text-lg">Close Chat?</h3>
            <p className="text-gray-600 mb-5 text-sm sm:mb-6 sm:text-base">
              Are you sure you want to close the chat? Your conversation will be lost.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
              <button
                onClick={() => setShowCloseConfirm(false)}
                className="bg-gray-200 text-gray-800 hover:bg-gray-300 active:bg-gray-400 order-2 min-h-[44px]
                         flex-1 touch-manipulation rounded-lg px-4
                         py-2.5 font-medium transition-colors
                         sm:order-1 sm:min-h-0"
              >
                No
              </button>
              <button
                onClick={confirmClose}
                className="order-1 min-h-[44px] flex-1 touch-manipulation rounded-lg px-4 py-2.5
                         font-medium text-white
                         transition-opacity hover:opacity-90 active:opacity-80
                         sm:order-2 sm:min-h-0"
                style={{ backgroundColor: '#ED6833' }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default withDatasourceCheck()<TechDocChatbotProps>(TechDocChatbot);
